import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type {
  CoachingOperationResult,
  CoachingRecordInput,
  CoachingSessionDto,
  FutureSeriesRescheduleRequest,
  InviteOperationRequest,
  SessionLifecycleRequest,
  SessionRescheduleRequest,
  SessionSeriesPreviewRequest,
  SessionSeriesRequest
} from "../services/coaching-dtos";
import { liveSessionRow } from "../services/coaching-live-readmodels";
import { getCoachingServices } from "../services/coaching-service-provider";
import { coachingQueryKeys, isAggregateSessionListContaining } from "./coaching-query-keys";
import { createIdempotencyKey, getMutationSideEffectSummary } from "./useCoachingQueries";

const services = getCoachingServices();

export function useSessionWorkflowMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      previewSeries: getMutationSideEffectSummary("sessions.previewSeries"),
      createSeries: getMutationSideEffectSummary("sessions.createSeries"),
      sendInvites: getMutationSideEffectSummary("sessions.sendInvites"),
      forceResend: getMutationSideEffectSummary("sessions.forceResend"),
      rescheduleOne: getMutationSideEffectSummary("sessions.rescheduleOne"),
      rescheduleFuture: getMutationSideEffectSummary("sessions.rescheduleFuture"),
      syncZoom: getMutationSideEffectSummary("sessions.syncZoom"),
      reassignCoach: getMutationSideEffectSummary("sessions.reassignCoach"),
      lifecycle: getMutationSideEffectSummary("sessions.lifecycle")
    },
    previewSeries: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: SessionSeriesPreviewRequest }) =>
        services.sessions.previewSeries(cohortId, body, createIdempotencyKey(`sessions-preview-series:${cohortId}`))
    }),
    createSeries: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: SessionSeriesRequest }) =>
        services.sessions.createSeries(cohortId, body, createIdempotencyKey(`sessions-create-series:${cohortId}`)),
      onSuccess: (result, { cohortId }) => cacheSessionSeriesWriteTruth(queryClient, cohortId, result)
    }),
    sendInvites: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: InviteOperationRequest }) =>
        services.sessions.sendInvites(sessionId, body, createIdempotencyKey(`sessions-send-invites:${sessionId}`)),
      onSuccess: (result, { sessionId }) => cacheSessionWriteTruth(queryClient, sessionId, result)
    }),
    forceResend: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: InviteOperationRequest }) =>
        services.sessions.forceResend(sessionId, body, createIdempotencyKey(`sessions-force-resend:${sessionId}`)),
      onSuccess: (result, { sessionId }) => cacheSessionWriteTruth(queryClient, sessionId, result)
    }),
    rescheduleOne: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: SessionRescheduleRequest }) =>
        services.sessions.rescheduleOne(sessionId, body, createIdempotencyKey(`sessions-reschedule-one:${sessionId}`)),
      onSuccess: (result, { sessionId }) => cacheSessionWriteTruth(queryClient, sessionId, result)
    }),
    rescheduleFuture: useMutation({
      mutationFn: ({ cohortId, body }: { cohortId: string; body: FutureSeriesRescheduleRequest }) =>
        services.sessions.rescheduleFuture(cohortId, body, createIdempotencyKey(`sessions-reschedule-future:${cohortId}`)),
      onSuccess: (result) => invalidateSessionWorkflowReadbacks(queryClient, result.id)
    }),
    syncZoom: useMutation({
      mutationFn: (sessionId: string) => services.sessions.syncZoom(sessionId, createIdempotencyKey(`sessions-sync-zoom:${sessionId}`)),
      onSuccess: (result, sessionId) => cacheSessionWriteTruth(queryClient, sessionId, result)
    }),
    reassignCoach: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: CoachingRecordInput }) =>
        services.sessions.reassignCoach(sessionId, body, createIdempotencyKey(`sessions-reassign-coach:${sessionId}`)),
      onSuccess: (result, { sessionId }) => cacheSessionWriteTruth(queryClient, sessionId, result)
    }),
    lifecycle: useMutation({
      mutationFn: ({ sessionId, body }: { sessionId: string; body: SessionLifecycleRequest }) =>
        services.sessions.lifecycle(sessionId, body, createIdempotencyKey(`sessions-lifecycle:${sessionId}`)),
      onSuccess: (result, { sessionId }) => cacheSessionWriteTruth(queryClient, sessionId, result)
    })
  };
}

/** WRITE-TRUTH (class 12): map a live session row from a mutation response. */
function sessionDtoFromWriteRecord(sessionRecord: Record<string, unknown>, sessionId: string): CoachingSessionDto {
  return { ...liveSessionRow(sessionRecord), id: String(sessionRecord.id ?? sessionId) };
}

function patchSessionIntoListCaches(queryClient: QueryClient, dto: CoachingSessionDto) {
  queryClient.setQueryData(coachingQueryKeys.sessionDetail(dto.id), dto);
  const patchList = (existing: { items?: CoachingSessionDto[] } | undefined) => {
    if (!existing?.items) return existing;
    if (!existing.items.some((row) => row.id === dto.id)) return existing;
    return { ...existing, items: existing.items.map((row) => (row.id === dto.id ? { ...row, ...dto } : row)) };
  };
  queryClient.setQueriesData({ queryKey: [...coachingQueryKeys.root, "list", "sessions.list"] }, patchList);
  if (dto.cohort_id) {
    const cohortId = dto.cohort_id;
    queryClient.setQueriesData(
      { queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }) },
      patchList
    );
    queryClient.setQueriesData({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) }, patchList);
  }
}

function patchSessionSeriesIntoListCaches(
  queryClient: QueryClient,
  cohortId: string,
  result: CoachingOperationResult
): boolean {
  const rawRows = Array.isArray(result.row_results) ? result.row_results : [];
  const dtos = rawRows
    .filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === "object")
    .map((row, index) => sessionDtoFromWriteRecord(row, String(row.id ?? `series-${cohortId}-${index}`)));
  if (!dtos.length) return false;

  for (const dto of dtos) {
    queryClient.setQueryData(coachingQueryKeys.sessionDetail(dto.id), dto);
  }

  const mergeIntoList = (existing: { items?: CoachingSessionDto[] } | undefined) => {
    if (!existing?.items) return { items: dtos, next_cursor: null };
    const byId = new Map(existing.items.map((row) => [row.id, row]));
    for (const dto of dtos) byId.set(dto.id, { ...byId.get(dto.id), ...dto });
    return { ...existing, items: Array.from(byId.values()) };
  };

  queryClient.setQueriesData({ queryKey: [...coachingQueryKeys.root, "list", "sessions.list"] }, mergeIntoList);
  queryClient.setQueriesData(
    { queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }) },
    mergeIntoList
  );
  queryClient.setQueriesData({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) }, mergeIntoList);
  return true;
}

/**
 * WRITE-TRUTH (AGENTS.md Hyperdrive law): session mutations that return a row patch
 * detail + list caches and skip sessionDetail invalidation (class 12).
 */
async function cacheSessionWriteTruth(
  queryClient: QueryClient,
  sessionId: string,
  result: CoachingOperationResult
) {
  const sessionRecord = result.session;
  if (sessionRecord && typeof sessionRecord === "object") {
    patchSessionIntoListCaches(queryClient, sessionDtoFromWriteRecord(sessionRecord, sessionId));
    await invalidateSessionWorkflowReadbacks(queryClient, undefined, null);
    return;
  }
  await invalidateSessionWorkflowReadbacks(queryClient, undefined, sessionId);
}

async function cacheSessionSeriesWriteTruth(
  queryClient: QueryClient,
  cohortId: string,
  result: CoachingOperationResult
) {
  if (patchSessionSeriesIntoListCaches(queryClient, cohortId, result)) {
    await invalidateSessionWorkflowReadbacks(queryClient, undefined, null);
    return;
  }
  await invalidateSessionWorkflowReadbacks(queryClient, cohortId);
}

async function invalidateSessionWorkflowReadbacks(
  queryClient: QueryClient,
  cohortId?: string,
  sessionId?: string | null
) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("history.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.recordScopeRoot() })
  ];
  if (cohortId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }) }),
      queryClient.invalidateQueries({ predicate: (query) => isAggregateSessionListContaining(query.queryKey, cohortId) })
    );
  }
  if (sessionId) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: coachingQueryKeys.sessionDetail(sessionId) }));
  }
  await Promise.all(invalidations);
}